import { z } from 'zod';
import { gstinSchema, gstRateSchema, hsnSacSchema } from '../money/gst.js';
import { SUPPLY_KINDS } from '../money/invoice-tax.js';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { dateInputSchema, isoDateStringSchema, objectIdSchema, stateCodeSchema } from './common.js';

/**
 * The document the customer takes away.
 *
 * Everything below is a SNAPSHOT. An issued invoice holds its own copy of the lines, the prices,
 * the tax breakdown, the customer's details and the shop's details exactly as they were at the
 * moment it was issued — so a shop that changes its address in November cannot alter the bill it
 * handed somebody in August, and re-pricing an order cannot rewrite the document already given
 * for it. That is why this file repeats fields that plainly exist on the order and the settings
 * document: a reference would let them drift, and a document that quietly changes after it was
 * handed over is not evidence of anything.
 *
 * There is no update shape here, and there will not be one. A correction is a CREDIT NOTE that
 * references the original, which is both what the law requires and what makes the trail readable
 * afterwards.
 */

export const INVOICE_KINDS = ['invoice', 'credit_note'] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];
export const invoiceKindSchema = z.enum(INVOICE_KINDS);

export const supplyKindSchema = z.enum(SUPPLY_KINDS);

/** The two widths a shop prints on: A4 for a file copy, 80mm for the counter's till roll. */
export const INVOICE_PAPER_SIZES = ['a4', 'thermal'] as const;
export type InvoicePaperSize = (typeof INVOICE_PAPER_SIZES)[number];
export const invoicePaperSizeSchema = z.enum(INVOICE_PAPER_SIZES);

export const INVOICE_NOTE_MAX_LENGTH = 500;
export const CREDIT_NOTE_REASON_MAX_LENGTH = 500;

// --- The frozen parties --------------------------------------------------------------------

/** The shop as it was when this document was issued. */
export const invoicePartySchema = z.object({
  name: z.string(),
  legalName: z.string().nullable(),
  addressLines: z.array(z.string()),
  stateName: z.string().nullable(),
  stateCode: stateCodeSchema.nullable(),
  pincode: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** Null on an unregistered shop's bill, where it must not appear at all. */
  gstin: z.string().nullable(),
  /**
   * Where the customer sends a transfer, already laid out as lines to print.
   *
   * FROZEN like everything else on this document. A shop that changes bank next year must not
   * change what last year's bills said to pay into — that is the whole reason this snapshot
   * exists. Empty for every shop that has not filled the section in, or has filled it in and
   * not asked for it on the bill, and the PDF then prints no block at all rather than a heading
   * with nothing under it.
   *
   * Defaulted, so every invoice issued before this field existed still parses.
   */
  bankLines: z.array(z.string()).default([]),
});
export type InvoiceParty = z.infer<typeof invoicePartySchema>;

/** The customer as they were when this document was issued. */
export const invoiceCustomerSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  phone: z.string(),
  addressLines: z.array(z.string()),
  stateCode: stateCodeSchema.nullable(),
  /** A B2B customer's own number, when they gave one. */
  gstin: z.string().nullable(),
});
export type InvoiceCustomer = z.infer<typeof invoiceCustomerSchema>;

// --- Lines and totals ------------------------------------------------------------------------

export const invoiceLineSchema = z.object({
  description: z.string(),
  /** Null throughout on an unregistered shop's bill. */
  hsnSac: z.string().nullable(),
  kind: supplyKindSchema,
  quantity: z.number(),
  unitPricePaise: nonNegativePaiseSchema,
  grossPaise: nonNegativePaiseSchema,
  discountPaise: nonNegativePaiseSchema,
  netPaise: paiseSchema,
  ratePercent: z.number(),
  taxablePaise: paiseSchema,
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  taxPaise: paiseSchema,
  totalPaise: paiseSchema,
});
export type InvoiceLine = z.infer<typeof invoiceLineSchema>;

export const invoiceRateSummaryRowSchema = z.object({
  ratePercent: z.number(),
  taxablePaise: paiseSchema,
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  taxPaise: paiseSchema,
  totalPaise: paiseSchema,
});
export type InvoiceRateSummaryRow = z.infer<typeof invoiceRateSummaryRowSchema>;

export const invoiceTotalsSchema = z.object({
  grossPaise: nonNegativePaiseSchema,
  discountPaise: nonNegativePaiseSchema,
  taxablePaise: paiseSchema,
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  taxPaise: paiseSchema,
  beforeRoundingPaise: paiseSchema,
  roundingOffPaise: paiseSchema,
  totalPaise: paiseSchema,
});
export type InvoiceTotals = z.infer<typeof invoiceTotalsSchema>;

// --- The document ------------------------------------------------------------------------------

export const invoiceSchema = z.object({
  id: objectIdSchema,
  kind: invoiceKindSchema,
  /** `BQ/25-26/0001`. Gapless per shop, per series, per financial year. */
  number: z.string(),
  financialYear: z.string(),
  issuedAt: isoDateStringSchema,
  orderId: objectIdSchema,
  orderNumber: z.string(),

  /**
   * Whether this document carries tax at all. False produces a plain bill: no GSTIN, no HSN,
   * no tax lines, and no tax wording anywhere — which is a legal requirement for a shop under
   * the threshold or on the composition scheme, not a display preference.
   */
  registered: z.boolean(),
  interState: z.boolean(),
  pricesIncludeTax: z.boolean(),
  placeOfSupplyStateCode: stateCodeSchema.nullable(),

  shop: invoicePartySchema,
  customer: invoiceCustomerSchema,
  lines: z.array(invoiceLineSchema),
  totals: invoiceTotalsSchema,
  rateSummary: z.array(invoiceRateSummaryRowSchema),
  /** Spelled out under the total, as an Indian invoice conventionally does. */
  amountInWords: z.string(),
  notes: z.string().nullable(),
  footerNote: z.string().nullable(),

  /**
   * What the ledger says right now — NOT part of the frozen document.
   *
   * The tax content of an invoice is immutable; what has been paid against the order it bills
   * is not, and a customer who settles a balance on Friday should not be handed a document that
   * insists they still owe it. So these are read live and the PDF stamps the moment it was
   * rendered (`pdfGeneratedAt`) beside them.
   */
  paidPaise: paiseSchema,
  balancePaise: paiseSchema,

  /** Set on a credit note: the invoice it corrects. */
  originalInvoiceId: objectIdSchema.nullable(),
  originalNumber: z.string().nullable(),
  reason: z.string().nullable(),
  /** Set on an invoice that a credit note has cancelled. */
  creditNoteId: objectIdSchema.nullable(),
  creditNoteNumber: z.string().nullable(),
  cancelledAt: isoDateStringSchema.nullable(),

  issuedByUserId: z.string().nullable(),
  issuedByName: z.string().nullable(),

  /** When the stored PDF was last rendered. Null until one has been. */
  pdfGeneratedAt: isoDateStringSchema.nullable(),
  /**
   * The A4 design this document prints in, recorded with its first PDF and kept for good, so a
   * reprint looks like the copy the customer already holds. Output only: nothing sets it but
   * rendering. Null until the first render (and on a bill rendered before designs existed, until
   * its next one records Classic). A plain string rather than the template list, because a
   * recorded design is history and must still read back if a template is ever retired.
   */
  pdfTemplate: z.string().nullable(),
  /** The recorded accent, `#rrggbb`. Null with `pdfTemplate`, and on a legacy bill recorded Classic. */
  pdfAccentColor: z.string().nullable(),
  /** When this document was last sent to the customer on WhatsApp. */
  sentOnWhatsappAt: isoDateStringSchema.nullable(),
  createdAt: isoDateStringSchema,
});
export type Invoice = z.infer<typeof invoiceSchema>;

// --- Issuing -------------------------------------------------------------------------------------

/**
 * A per-line override, for the shop that bills one garment at a different rate or under a
 * different code than its own default. Identified by the order item, so a client cannot invent
 * a line that is not on the order — the amounts always come from the order, never from here.
 */
export const invoiceLineOverrideSchema = z.object({
  /** An order item's id, or an order product line's id — both are keys into the same order. */
  orderItemId: objectIdSchema,
  hsnSac: hsnSacSchema.optional(),
  ratePercent: gstRateSchema.optional(),
});
export type InvoiceLineOverride = z.infer<typeof invoiceLineOverrideSchema>;

export const issueInvoiceSchema = z.object({
  /** Defaults to now. A bill typed up the next morning is dated when the shop says it was. */
  issuedAt: dateInputSchema.optional(),
  /**
   * Where the supply is treated as made. Defaults to the shop's own state, which is every
   * walk-in customer; a shop couriering to another state sets it and gets IGST.
   */
  placeOfSupplyStateCode: stateCodeSchema.optional(),
  /** A registered customer's own number, printed on the document for their input credit. */
  customerGstin: gstinSchema.nullish(),
  notes: z.string().trim().max(INVOICE_NOTE_MAX_LENGTH).nullish(),
  lineOverrides: z.array(invoiceLineOverrideSchema).max(50).optional(),
});
export type IssueInvoiceInput = z.input<typeof issueInvoiceSchema>;

export const creditNoteSchema = z.object({
  /** Required. A credit note without a stated reason is the argument it exists to end. */
  reason: z
    .string()
    .trim()
    .min(3, 'Say why this invoice is being credited')
    .max(CREDIT_NOTE_REASON_MAX_LENGTH),
  issuedAt: dateInputSchema.optional(),
});
export type CreditNoteInput = z.input<typeof creditNoteSchema>;

/** Sending the document to the customer on WhatsApp. */
export const sendInvoiceSchema = z.object({
  /** Defaults to the customer's number on the order. */
  to: z.string().trim().optional(),
  paperSize: invoicePaperSizeSchema.default('a4'),
  caption: z.string().trim().max(400).optional(),
});
export type SendInvoiceInput = z.input<typeof sendInvoiceSchema>;

// --- The sales register --------------------------------------------------------------------------

/**
 * What the accountant asks for at filing time: everything issued in a period, with the totals
 * broken down by tax rate. Credit notes are included and carry negative amounts, because a
 * register that omitted them would overstate the shop's output tax.
 */
export const salesRegisterQuerySchema = z.object({
  from: dateInputSchema,
  to: dateInputSchema,
  kind: invoiceKindSchema.optional(),
});
export type SalesRegisterQuery = z.infer<typeof salesRegisterQuerySchema>;

export const salesRegisterRowSchema = z.object({
  id: objectIdSchema,
  kind: invoiceKindSchema,
  number: z.string(),
  issuedAt: isoDateStringSchema,
  orderNumber: z.string(),
  customerName: z.string(),
  customerGstin: z.string().nullable(),
  placeOfSupplyStateCode: stateCodeSchema.nullable(),
  taxablePaise: paiseSchema,
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  taxPaise: paiseSchema,
  totalPaise: paiseSchema,
  cancelledAt: isoDateStringSchema.nullable(),
});
export type SalesRegisterRow = z.infer<typeof salesRegisterRowSchema>;

export const salesRegisterSchema = z.object({
  from: isoDateStringSchema,
  to: isoDateStringSchema,
  registered: z.boolean(),
  rows: z.array(salesRegisterRowSchema),
  byRate: z.array(invoiceRateSummaryRowSchema),
  totals: z.object({
    documentCount: z.number().int().nonnegative(),
    taxablePaise: paiseSchema,
    cgstPaise: paiseSchema,
    sgstPaise: paiseSchema,
    igstPaise: paiseSchema,
    taxPaise: paiseSchema,
    totalPaise: paiseSchema,
  }),
});
export type SalesRegister = z.infer<typeof salesRegisterSchema>;

/** One order's documents, for the invoice card on the order screen. */
export const orderInvoicesSchema = z.object({
  orderId: objectIdSchema,
  orderNumber: z.string(),
  /** False when the shop is not GST-registered — the card then says "bill", not "tax invoice". */
  registered: z.boolean(),
  invoices: z.array(invoiceSchema),
});
export type OrderInvoices = z.infer<typeof orderInvoicesSchema>;

/** What a download endpoint answers with: a short-lived link straight to the stored PDF. */
export const invoiceDownloadSchema = z.object({
  url: z.string(),
  expiresAt: isoDateStringSchema,
  filename: z.string(),
  paperSize: invoicePaperSizeSchema,
});
export type InvoiceDownload = z.infer<typeof invoiceDownloadSchema>;
